import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Switch } from '@/app/components/ui/switch';
import { toast } from '@/app/components/ui/use-toast';
import { cn } from '@/app/utils';
import { useChannelBind } from '../../_hook/use-channel-bind';
import type { QrcodeChannelItem } from './types';

interface ChannelBindModalProps {
  channel: QrcodeChannelItem;
  clawId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

type TimerRef = ReturnType<typeof setTimeout>;

export const ChannelBindModal = ({ channel, clawId, open, onOpenChange, onSuccess }: ChannelBindModalProps) => {
  const t = useTranslations('claw.channelConfig');
  const ChannelIcon = channel.icon;
  const closeTimerRef = useRef<TimerRef | null>(null);

  const handleBindSuccess = useCallback(() => {
    onSuccess?.();
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      onOpenChange(false);
    }, 1500);
  }, [onOpenChange, onSuccess]);

  const handleBindError = useCallback((error: string) => {
    toast({ type: 'error', message: error });
  }, []);

  const {
    status,
    qrDataUri,
    errorMessage,
    enabled,
    isConfigLoading,
    isConfigSaving,
    loadEnabled,
    updateEnabled,
    startBind,
    reset,
  } = useChannelBind({
    clawId,
    channelId: channel.id,
    provider: channel.provider,
    onSuccess: handleBindSuccess,
    onError: handleBindError,
  });

  useEffect(() => {
    if (!open) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      reset();
      return;
    }

    let cancelled = false;

    const bootstrap = async () => {
      const { enabled: nextEnabled } = await loadEnabled();

      if (!cancelled && nextEnabled) {
        await startBind();
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleEnabledChange = async (nextEnabled: boolean) => {
    const { error } = await updateEnabled(nextEnabled);

    if (error) {
      toast({ type: 'error', message: error });
      return;
    }

    if (!nextEnabled) {
      reset();
      return;
    }

    await startBind();
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const showQr = status === 'polling' && Boolean(qrDataUri);
  const showLoading = status === 'loading' || (status === 'idle' && enabled);
  const showError = status === 'error';
  const showExpired = status === 'expired';
  const showSuccess = status === 'success';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        inheritTheme
        className="w-[416px] max-w-[calc(100vw-32px)] gap-0 border border-system-white12 bg-[#121316] light:bg-system-bg02 p-0 shadow-[0px_30px_200px_0px_rgba(0,0,0,0.48)]"
        closeClassName="top-5 right-5"
      >
        <DialogHeader className="border-system-white12 border-b px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="flex items-center gap-2 font-medium text-base text-system-text01">
              <ChannelIcon className="size-5" />
              <span>{t(channel.modalTitleKey)}</span>
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="px-6 py-6">
          <div className="flex min-h-[300px] flex-col items-center justify-center gap-2">
            <div
              className={cn(
                'flex size-[180px] items-center justify-center overflow-hidden rounded-12 bg-system-white08 light:bg-system-bg03',
                showQr && 'bg-white p-2',
              )}
            >
              {showQr ? (
                <img src={qrDataUri} alt={t('bind.qrCodeAlt')} className="size-full rounded-8 object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-3 px-4 text-center">
                  {showLoading && (
                    <>
                      <div className="size-8 animate-spin rounded-full border-2 border-system-white24 border-t-system-white" />
                      <p className="text-sm text-system-text03">{t('bind.generatingQrCode')}</p>
                    </>
                  )}
                  {!enabled && !showError && !showExpired && !showSuccess && (
                    <p className="text-center text-sm text-system-text03">
                      {t('fields.enabled.description', { channel: t(channel.titleKey.menu) })}
                    </p>
                  )}
                  {showError && (
                    <>
                      <BaseLineClose className="size-8 text-red-400" />
                      <p className="text-sm text-system-text01">{t('bind.failed')}</p>
                      {errorMessage && <p className="text-system-text03 text-xs">{errorMessage}</p>}
                      <button
                        type="button"
                        className="rounded-8 bg-system-white12 px-3 py-1 text-system-text01 text-xs transition-colors hover:bg-system-white20"
                        onClick={startBind}
                      >
                        {t('common.retry')}
                      </button>
                    </>
                  )}
                  {showExpired && (
                    <>
                      <p className="text-sm text-system-text01">{t('bind.expired')}</p>
                      <button
                        type="button"
                        className="rounded-8 bg-system-white12 px-3 py-1 text-system-text01 text-xs transition-colors hover:bg-system-white20"
                        onClick={startBind}
                      >
                        {t('bind.refreshQrCode')}
                      </button>
                    </>
                  )}
                  {showSuccess && <p className="text-sm text-system-text01">{t('bind.success')}</p>}
                </div>
              )}
            </div>

            <p className="mt-4 text-center text-sm text-system-text01">{t('bind.scanToBind')}</p>
            <p className="text-system-text04 text-xs">{t('bind.qrExpiresInFiveMinutes')}</p>
            <p className="text-center text-system-text04 text-xs">{t(`bind.relationshipTip.${channel.id}`)}</p>
          </div>

          <div className="my-6 h-px w-full bg-system-white12" />

          <div className="flex items-center justify-between gap-4">
            <p className="font-medium text-sm text-system-text01">
              {t('fields.enabled.label', { channel: t(channel.titleKey.menu) })}
            </p>
            <Switch
              checked={enabled}
              onCheckedChange={(checked) => {
                void handleEnabledChange(checked);
              }}
              disabled={isConfigLoading || isConfigSaving}
              className="h-7 w-12 data-[state=checked]:bg-[#5765F2] data-[state=unchecked]:bg-system-white24"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
